import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { Container } from '@mui/material';
import {
    ElectricBolt, Layers, Percent, Tune, Schema, CloudOff, Map, Lock, Dashboard,
    Psychology, Visibility, Loop, Hub, Groups, AccountTree, AccountBalance, Balance,
    Analytics, PlaylistAddCheck, Grade, Rule, FolderCopy, FactCheck, History, Flag,
    Shield, PersonSearch, GridView, CorporateFare, Timer, NotificationsActive,
    AdminPanelSettings, Policy, Api, OnlinePrediction, ConfirmationNumber, Badge,
    EventNote, DynamicForm, ListAlt, Payments, Devices, Domain, QrCode2, Storefront,
    Handshake, Calculate, Paid, EmojiEvents, Campaign, SmartButton, PermMedia,
    Loyalty, PieChart, SwapVert, Recommend, MilitaryTech, QueryStats, ManageSearch,
    CheckCircle, ArrowBack, ArrowOutward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import TopHeader from '../Components/TopHeader';
import Waves from '../Components/ui/Waves';
import SectionHeader from '../Components/ui/SectionHeader';
import Reveal from '../Components/ui/Reveal';
import CtaBand from '../Components/ui/CtaBand';
import { PRODUCTS, pick } from '../Data/Products';

const ICONS: Record<string, React.ReactNode> = {
    bolt: <ElectricBolt />, layers: <Layers />, percent: <Percent />, tune: <Tune />,
    workflow: <Schema />, offline: <CloudOff />, map: <Map />, lock: <Lock />,
    dashboard: <Dashboard />, ai: <Psychology />, visibility: <Visibility />,
    evolve: <Loop />, hub: <Hub />, committee: <Groups />, matrix: <AccountTree />,
    finance: <AccountBalance />, scale: <Balance />, analytics: <Analytics />,
    checklist: <PlaylistAddCheck />, score: <Grade />, rule: <Rule />,
    folder: <FolderCopy />, compliance: <FactCheck />, history: <History />,
    flag: <Flag />, shield: <Shield />, person: <PersonSearch />, grid: <GridView />,
    entities: <CorporateFare />, timer: <Timer />, bell: <NotificationsActive />,
    roles: <AdminPanelSettings />, audit: <Policy />, api: <Api />,
    predict: <OnlinePrediction />, booking: <ConfirmationNumber />, kyc: <Badge />,
    calendar: <EventNote />, form: <DynamicForm />, catalog: <ListAlt />,
    cash: <Payments />, omni: <Devices />, sectors: <Domain />, code: <QrCode2 />,
    network: <Hub />, opps: <CheckCircle />, store: <Storefront />,
    affiliate: <Handshake />, calc: <Calculate />, pay: <Paid />,
    trophy: <EmojiEvents />, campaign: <Campaign />, automation: <SmartButton />,
    library: <PermMedia />, loyalty: <Loyalty />, segment: <PieChart />,
    swap: <SwapVert />, recommend: <Recommend />, game: <MilitaryTech />,
    bi: <QueryStats />, trace: <ManageSearch />,
};

function ProductPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const product = PRODUCTS.find((p) => p.slug === slug);

    if (!product) return <Navigate to="/#products" replace />;

    return (
        <div className="ibg-page">
            <Helmet>
                <title>{`${product.name} — IBG Alpha Technology`}</title>
                <meta name="description" content={pick(product.tagline, lang)} />
            </Helmet>

            <TopHeader />
            <Header />

            <section className="ibg-hero prod-hero">
                <Waves />
                <img className="ibg-hero__media prod-hero__bg" src={product.showcase.light} alt="" aria-hidden="true" />
                <div className="ibg-hero__veil" />
                <Container maxWidth="lg" className="ibg-hero__inner">
                    <div className="prod-hero__split">
                        <div>
                            <Reveal>
                                <button className="ibg-link ibg-link--back" onClick={() => navigate('/#products')}>
                                    <ArrowBack fontSize="small" /> {t('Product.back')}
                                </button>
                            </Reveal>
                            <Reveal delay={0.05}>
                                {product.logo && (
                                    <img className="prod-hero__logo" src={product.logo} alt={product.name} />
                                )}
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h1 className="ibg-display prod-hero__title">{product.name}</h1>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="ibg-hero__lead prod-hero__tagline">{pick(product.tagline, lang)}</p>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="ibg-body prod-hero__lead">{pick(product.lead, lang)}</p>
                            </Reveal>
                            <Reveal delay={0.25} className="ibg-actions">
                                <button className="ibg-btn ibg-btn--light" onClick={() => navigate('/contact')}>
                                    {t('Product.demo')} <ArrowOutward fontSize="small" />
                                </button>
                                <button className="ibg-btn ibg-btn--outline-light" onClick={() => navigate('/contact')}>
                                    {t('Product.talk')}
                                </button>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className="prod-hero__media">
                            <figure className="prod-shot">
                                <span className="prod-shot__bar"><i /><i /><i /></span>
                                <img className={product.showcase.dark ? 'prod-shot__img prod-shot__img--light' : 'prod-shot__img'} src={product.showcase.light} alt={product.showcase.alt} />
                                {product.showcase.dark && (
                                    <img className="prod-shot__img prod-shot__img--dark" src={product.showcase.dark} alt={product.showcase.alt} />
                                )}
                            </figure>
                            {product.showcase2 && (
                                <figure className="prod-shot prod-shot--float">
                                    <img className="prod-shot__img" src={product.showcase2.src} alt={product.showcase2.alt} />
                                </figure>
                            )}
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={product.name}
                        title={t('Product.featuresTitle')}
                        description={t('Product.featuresDesc')}
                    />
                    <div className="prod-features">
                        {product.features.map((f, i) => (
                            <Reveal key={i} delay={(i % 3) * 0.06} className="prod-feature">
                                <span className="prod-feature__icon">{ICONS[f.icon] || <CheckCircle />}</span>
                                <h3 className="prod-feature__title">{pick(f.title, lang)}</h3>
                                <p className="ibg-small">{pick(f.desc, lang)}</p>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="ibg-section ibg-section--dark prod-oneliner">
                <Waves />
                <Container maxWidth="lg">
                    <Reveal>
                        <span className="ibg-eyebrow ibg-eyebrow--light">{product.name} {t('Product.oneliner')}</span>
                        <p className="prod-oneliner__text">{pick(product.oneliner, lang)}</p>
                        {product.signature && <p className="prod-oneliner__sign">{pick(product.signature, lang)}</p>}
                    </Reveal>
                </Container>
            </section>

            <CtaBand title={t('Product.ctaTitle')} text={t('Product.ctaText')}>
                <button className="ibg-btn ibg-btn--light" onClick={() => navigate('/contact')}>
                    {t('Product.demo')} <ArrowOutward fontSize="small" />
                </button>
            </CtaBand>

            <Footer />
        </div>
    );
}

export default ProductPage;
